import { useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { SectionHeading } from "../components/SectionHeading";
import { SCREENS } from "../data/screens";
import { useSwipe } from "../hooks/useSwipe";
import { RichText, useSiteTranslation } from "../i18n";
import "../styles/gallery.css";

// Card text comes from home.gallery.cards in the site translations, in this order.
const CARD_SCREENS = [
  SCREENS.discover,
  SCREENS.chats,
  SCREENS.privateChat,
  SCREENS.privacy,
  SCREENS.profile,
  SCREENS.guidance,
  SCREENS.shorts,
  SCREENS.chatsDark,
];

const pad = (n) => String(n).padStart(2, "0");

/** Offset of a card from the current one, wrapped so the wheel loops both ways. */
function wheelOffset(index, current, count) {
  const halfway = Math.floor(count / 2);
  let offset = index - current;
  if (offset > halfway) offset -= count;
  if (offset < -halfway) offset += count;
  return offset;
}

export function ScreensGallery() {
  const [current, setCurrent] = useState(0);
  const cardRefs = useRef([]);
  const { t, dir } = useSiteTranslation();
  // In Arabic the wheel runs the other way: later screens sit to the left, and swipes and arrow keys follow.
  const flow = dir === "rtl" ? -1 : 1;
  const cards = t("home.gallery.cards", { returnObjects: true }).map((card, index) => ({ ...card, screen: CARD_SCREENS[index] }));
  const count = cards.length;
  const rotate = (direction) => setCurrent((index) => (index + direction + count) % count);
  const swipeHandlers = useSwipe((direction) => rotate(direction * flow));

  const onKeyDown = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const step = (event.key === "ArrowRight" ? 1 : -1) * flow;
    const next = (current + step + count) % count;
    setCurrent(next);
    cardRefs.current[next]?.focus({ preventScroll: true });
  };

  return (
    <section className="section experience" id="experience">
      <div className="wrap">
        <SectionHeading
          layout="gallery"
          eyebrow={t("home.gallery.eyebrow")}
          title={<RichText k="home.gallery.title" />}
        >
          <div className="gallery-controls">
            <button type="button" aria-label={t("home.gallery.previous")} onClick={() => rotate(-1)}>
              <Icon name="left" />
            </button>
            <span className="gallery-count" aria-live="polite">
              {pad(current + 1)} / {pad(count)}
            </span>
            <button type="button" aria-label={t("home.gallery.next")} onClick={() => rotate(1)}>
              <Icon name="arrow" />
            </button>
          </div>
        </SectionHeading>
      </div>

      <div className="screen-rail" onKeyDown={onKeyDown} {...swipeHandlers}>
        {cards.map(({ screen, title, caption }, index) => {
          const offset = wheelOffset(index, current, count);
          const active = offset === 0;
          return (
            <button
              key={title}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              className="screen-card"
              type="button"
              data-wheel-offset={offset * flow}
              tabIndex={active ? 0 : -1}
              aria-current={active ? "true" : undefined}
              onClick={() => !active && setCurrent(index)}
            >
              <span className="screen-number">{pad(index + 1)}</span>
              <div className="screen-phone">
                <img src={screen.src} alt={t(screen.alt)} draggable="false" />
              </div>
              <div className="screen-meta">
                <b>{title}</b>
                <small>{caption}</small>
              </div>
            </button>
          );
        })}
      </div>
      <p className="gallery-hint">{t("home.gallery.hint")}</p>
    </section>
  );
}
